import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Link,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { serviceRequestsActions } from "../state/serviceRequests/actions";
import { uiSelectors, useDispatch, useSelector } from "../state";
import ServiceUpload from "./admin/components/ServiceUpload";
import { useMobile } from "../themes";
import Sidebar from "../layouts/sidebar/Sidebar";
import { REACT_APP_ASSETS_URL } from "../config";

// Define form validation schema
const validationSchema = Yup.object().shape({
  first_name: Yup.string().required("Required"),
  last_name: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email format").required("Required"),
  phone: Yup.string().nullable(),
  request_type: Yup.string().required("Required"),
  policy_carrier: Yup.string().required("Required"),
  policy_type: Yup.string().required("Required"),
  policy_number: Yup.string().required("Required"),
  description: Yup.string().required("Required"),
  preferred_contact_method: Yup.string().required("Required"),
});

// Initial values for the form`
const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  policy_carrier: "",
  policy_type: "",
  policy_number: "",
  description: "",
  preferred_contact_method: "email",
  request_type: "",
  due_date: null,
  // Files will be handled separately
};

// Policy type options
const policyTypes = [
  { value: "auto", label: "Auto" },
  { value: "home", label: "Home" },
  { value: "life", label: "Life" },
  { value: "commercial", label: "Commercial" },
  { value: "other", label: "Other" },
];

// Request type options
const requestTypes = [
  { value: "policy_change", label: "Policy Change" },
  { value: "mortgage_change", label: "Mortgage Change" },
  { value: "billing", label: "Billing" },
  { value: "other", label: "Other" },
];

export default function ServiceRequestForm() {
  const [files, setFiles] = useState<FormData>(new FormData());
  const dispatch = useDispatch();
  const isMobile = useMobile();
  const { logo_height, logo_path } = useSelector(uiSelectors.agencyData);
  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    dispatch(serviceRequestsActions.submitServiceRequest(values, files));
    setSubmitting(false);
    resetForm();
    setFiles(new FormData());
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexDirection: isMobile ? "column" : "row",
        overflow: "hidden",
      }}
    >
      {isMobile && (
        <Box
          sx={{
            padding: "32px 30px",
            margin: "auto",
            "& img": {
              maxWidth: 255,
              height: logo_height || "80px",
            },
          }}
        >
          {logo_path && (
            <Link href="/">
              <img src={`${REACT_APP_ASSETS_URL}/${logo_path}`} alt="logo" />
            </Link>
          )}
        </Box>
      )}
      {!isMobile && (
        <Sidebar mobileOpen={undefined} setMobileOpen={undefined} />
      )}
      <Box
        sx={{
          height: isMobile ? "auto" : "calc(100vh - 64px)",
          overflowY: "auto",
          width: "100%",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Container
          maxWidth="md"
          sx={{ py: isMobile ? 0 : 4, mt: isMobile ? 0 : "40px", flexGrow: 1 }}
        >
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" component="h1" gutterBottom>
              Service Request Form
            </Typography>
          </Box>
          <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
            <Box textAlign="center" mb={4}>
              <Typography variant="body1" color="textSecondary">
                Please fill out the form below to submit your service request
              </Typography>
            </Box>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form>
                  <Grid container spacing={3}>
                    {/* First Name */}
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="first_name"
                        label="First Name"
                        variant="outlined"
                        error={touched.first_name && Boolean(errors.first_name)}
                        helperText={touched.first_name && errors.first_name}
                      />
                    </Grid>

                    {/* Last Name */}
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="last_name"
                        label="Last Name"
                        variant="outlined"
                        error={touched.last_name && Boolean(errors.last_name)}
                        helperText={touched.last_name && errors.last_name}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="phone"
                        label="Phone Number"
                        variant="outlined"
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                      <ErrorMessage name="at-least-one-contact" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="email"
                        label="Email Address"
                        variant="outlined"
                        type="email"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                      <ErrorMessage name="at-least-one-contact" />
                    </Grid>

                    {/* Policy Carrier */}
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="policy_carrier"
                        label="Policy Carrier"
                        variant="outlined"
                        error={
                          touched.policy_carrier &&
                          Boolean(errors.policy_carrier)
                        }
                        helperText={
                          touched.policy_carrier && errors.policy_carrier
                        }
                      />
                    </Grid>

                    {/* Policy Type */}
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        select
                        fullWidth
                        name="policy_type"
                        label="Policy Type"
                        variant="outlined"
                        error={
                          touched.policy_type && Boolean(errors.policy_type)
                        }
                        helperText={touched.policy_type && errors.policy_type}
                      >
                        {policyTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>

                    {/* Policy Number */}
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="policy_number"
                        label="Policy Number"
                        variant="outlined"
                        error={
                          touched.policy_number && Boolean(errors.policy_number)
                        }
                        helperText={
                          touched.policy_number && errors.policy_number
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        select
                        fullWidth
                        name="request_type"
                        label="Request Type"
                        variant="outlined"
                        error={
                          touched.request_type && Boolean(errors.request_type)
                        }
                        helperText={touched.request_type && errors.request_type}
                      >
                        {requestTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>
                    {/* Service Request Description */}
                    <Grid item xs={12}>
                      <Field
                        as={TextField}
                        fullWidth
                        multiline
                        rows={4}
                        name="description"
                        label="Describe Your Service Request"
                        variant="outlined"
                        error={
                          touched.description && Boolean(errors.description)
                        }
                        helperText={touched.description && errors.description}
                      />
                    </Grid>

                    {/* Due Date */}
                    <Grid item xs={12} sm={6}>
                      <Field name="due_date">
                        {({ field, form }) => (
                          <DatePicker
                            label="Requested Completion Date"
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue("due_date", value);
                            }}
                            minDate={new Date()}
                          />
                        )}
                      </Field>
                    </Grid>

                    {/* Preferred Contact Method */}
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Preferred Contact Method
                        </FormLabel>
                        <Field
                          as={RadioGroup}
                          name="preferred_contact_method"
                          row
                        >
                          <FormControlLabel
                            value="email"
                            control={<Radio />}
                            label="Email"
                          />
                          <FormControlLabel
                            value="phone"
                            control={<Radio />}
                            label="Phone"
                          />
                        </Field>
                        {touched.preferred_contact_method &&
                          errors.preferred_contact_method && (
                            <Typography color="error" variant="caption">
                              {errors.preferred_contact_method}
                            </Typography>
                          )}
                      </FormControl>
                    </Grid>

                    {/* Document Upload */}
                    <Grid item xs={12}>
                      <ServiceUpload setFiles={setFiles} />
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12} sx={{ textAlign: "center", mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={isSubmitting}
                        sx={{ minWidth: { xs: "100%", sm: "200px" } }}
                      >
                        {isSubmitting ? "Submitting..." : "Submit Request"}
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
